/** How an item that is waiting on the person is named and where it opens - shared by the first tab of
 * Action items and Home's "Needs you" list, so a row reads and goes the same way in both.
 *
 * `actionFor` (`homeWords.ts`) is the one place that turns a work item into a destination from what
 * the server says to do (`fix.action`); an approval is the exception, because its decision is on the
 * record's Workflow tab and not at a list.
 */

import type { WaitingItem } from "../api/waiting";
import { shortDate } from "../lib/format";
import { actionFor } from "./homeWords";

export const TYPE_LABEL: Record<string, string> = {
  approval_waiting: "Approval",
  document_to_review: "Bill to review",
  task: "Task",
  missing_data: "Missing data",
  boundary_question: "Boundary question",
  difference_to_explain: "Difference to explain",
  supplier_overdue: "Supplier overdue",
};

/** A filing's approval - to send it or to decide it - is a *Filing* in the Type column: the item is
 * about the filing as a whole, and "Approval" alone reads as one of a record's figures. */
function aboutAFiling(item: WaitingItem): boolean {
  // `detail` is always an object from the server; read as possibly absent because a row is a pointer
  // and a type label must never be what throws.
  const detail = item.detail as Record<string, unknown> | undefined;
  return item.kind === "approval_waiting" && detail?.record_kind === "filing_obligation";
}

/** A filing that is ready to be *sent* for approval: work for the person (an Action item), and not an
 * approval request waiting on a decision. The server derives it under the `approval_waiting` kind
 * because the next act is the approval route's, and says so by its fix (`request_approval`); a
 * request somebody must decide has no such fix. The queue (`/approval-requests?awaiting_me`) holds
 * only the latter, so the screen marks the two apart rather than counting one as the other. */
export function isFilingToSend(item: WaitingItem): boolean {
  return item.kind === "approval_waiting" && item.fix?.action === "request_approval";
}

/** What kind of row this is, for the screen's own attribute: the item's kind, except that a filing to
 * send is its own thing and is never counted among the requests waiting on a decision. */
export function rowKind(item: WaitingItem): string {
  return isFilingToSend(item) ? "filing_to_send" : item.kind;
}

export function typeOf(item: WaitingItem): string {
  if (aboutAFiling(item)) return "Filing";
  return TYPE_LABEL[item.kind] ?? item.kind.replace(/_/g, " ");
}

/** Where an item opens. An approval goes to the record's Workflow tab (where the decision is); every
 * other kind goes where `actionFor` says. */
export function destination(item: WaitingItem): { to: string; search?: Record<string, string> } {
  if (item.kind === "approval_waiting" && item.record_id && !isFilingToSend(item)) {
    return { to: `/records/${item.record_id}`, search: { tab: "workflow" } };
  }
  // A filing waiting to be sent opens on the filing; everything else where `actionFor` says.
  const action = actionFor(item);
  return action.search ? { to: action.to, search: action.search } : { to: action.to };
}

export function dueText(item: WaitingItem): string {
  return item.due ? shortDate(item.due) : "";
}
